import {
  Box,
  Center,
  Link as ChakraLink,
  Collapse,
  Flex,
  HStack,
  Image,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoSvg from "../../assets/brand.svg";
import { useAuth } from "../../hooks/useAuth";
import { sideBarRoutes } from "../../utils/constants";

const SideBar = ({
  isMobile,
  onClose,
}: {
  isMobile?: boolean;
  onClose?: () => void;
}) => {
  const [currMenu, setCurrMenu] = useState("");
  const { pathname } = useLocation();

  const { logout } = useAuth();
  const navigate = useNavigate();

  const checkPath = (arr: string[] | undefined): boolean => {
    if (!arr) return false;

    return arr.some((x) => x === pathname || pathname.includes(x));
  };

  useEffect(() => {
    setCurrMenu(pathname);
  }, [pathname]);

  return (
    <Box
      userSelect={"none"}
      pos={"relative"}
      w={isMobile ? "100%" : "230px"}
      bg="brand"
      zIndex={1}
      transition="all .4s"
    >
      <Center
        height={"74px"}
        w={"full"}
        borderBottom={"1px solid transparent"}
        borderBottomColor={useColorModeValue("neutral.200", "gray.700")}
      >
        <Link to={"/"}>
          <Image
            src={logoSvg}
            width={"100%"}
            maxW={"140px"}
            height={"52px"}
            alt="PAR LOGO"
            objectFit={"contain"}
            objectPosition={"center"}
          />
        </Link>
      </Center>

      <Box
        overflowY={"scroll"}
        className="hideSB"
        borderRight={"1px solid transparent"}
        borderRightColor={useColorModeValue("neutral.200", "gray.800")}
        pl={2}
        transition={"top 0.35s ease"}
      >
        <Flex
          flexDir={"column"}
          px={"12px"}
          h="calc(100vh - 74px)"
          pt={"58px"}
          pb={4}
        >
          {sideBarRoutes.map((route, idx) => {
            const Icon = route.icon;

            return route.path ? (
              <ChakraLink
                key={idx}
                mb={3}
                as={Link}
                to={route.path}
                onClick={
                  isMobile && onClose
                    ? () => {
                        onClose();
                      }
                    : undefined
                }
                display={"block"}
                color={route.path === pathname ? "white" : "#C8D1E0"}
                role={"group"}
                borderRadius={"2px"}
                bg={route.path === pathname ? "#324154" : "transparent"}
                _hover={{ bg: "#324154", color: "white" }}
                className="slow-transition"
              >
                <HStack px={"16px"} py={"10px"}>
                  <Box
                    width={isMobile ? "38px" : "22px"}
                    color={route.path === pathname ? "brand.500" : "text.200"}
                  >
                    <Icon
                      width={isMobile ? 36 : 18}
                      height={isMobile ? 36 : 18}
                    />
                  </Box>
                  <Text
                    as="span"
                    textStyle={isMobile ? "large" : "normal"}
                    color={route.path === pathname ? "white" : "#C8D1E0"}
                    _groupHover={{ color: "white" }}
                    className="slow-transition"
                  >
                    {route.title}
                  </Text>
                </HStack>
              </ChakraLink>
            ) : (
              <Box
                key={idx}
                mt={route.title === "Logout" ? "auto" : 0}
                mb={3}
                borderRadius={"2px"}
                className="slow-transition"
                bg={
                  checkPath(
                    route.children ? route.children?.map((x) => x.path) : [],
                  )
                    ? "#324154"
                    : "transparent"
                }
              >
                <HStack
                  px={"16px"}
                  py={"10px"}
                  width={"100%"}
                  role={"group"}
                  cursor={"pointer"}
                  borderRadius={"2px"}
                  _hover={{ bg: "#324154", color: "white" }}
                  className="slow-transition"
                  onClick={() => {
                    if (route.title === "Logout") {
                      logout();
                    } else {
                      if (currMenu === route.title) {
                        setCurrMenu("");
                      } else {
                        setCurrMenu(route.title);
                      }
                    }
                  }}
                >
                  <Box
                    width={isMobile ? "40px" : "22px"}
                    color={route.path === pathname ? "white" : "#C8D1E0"}
                    _groupHover={{
                      color: "white",
                    }}
                    className="slow-transition"
                  >
                    <Icon
                      width={isMobile ? 36 : 19}
                      height={isMobile ? 36 : 18}
                      style={{ paddingLeft: isMobile ? "2px" : 0 }}
                    />
                  </Box>
                  <Text
                    flex={1}
                    as="span"
                    textStyle={isMobile ? "large" : "normal"}
                    color={route.path === pathname ? "white" : "#C8D1E0"}
                    _groupHover={{
                      color: "white",
                    }}
                    className="slow-transition"
                  >
                    {route.title}
                  </Text>

                  {route.title !== "Logout" ? (
                    <Box
                      width={"20px"}
                      color={route.path === pathname ? "white" : "#C8D1E0"}
                      _groupHover={{
                        color: "white",
                      }}
                      className="slow-transition"
                    >
                      {currMenu === route.title ||
                      checkPath(
                        route.children
                          ? route.children?.map((x) => x.path)
                          : [],
                      ) ? (
                        <ChevronUp width={18} />
                      ) : (
                        <ChevronDown width={18} />
                      )}
                    </Box>
                  ) : null}
                </HStack>

                <Collapse
                  in={
                    currMenu === route.title ||
                    checkPath(
                      route.children ? route.children?.map((x) => x.path) : [],
                    )
                  }
                  animateOpacity
                >
                  <Box pb={4}>
                    {route.children?.map((item, ind) => {
                      return !isMobile ? (
                        <Box key={ind}>
                          {item.title === "Logout" ? (
                            <Text
                              cursor={"pointer"}
                              pl={"34px"}
                              mt={3}
                              w={"full"}
                              onClick={() => {
                                logout();
                              }}
                              textDecoration={"none"}
                              display={"flex"}
                              alignItems={"center"}
                              fontSize={"0.8rem"}
                              fontWeight={400}
                              color={
                                pathname === item.path || checkPath([item.path])
                                  ? "white"
                                  : "#C8D1E0"
                              }
                              _hover={{ color: "white" }}
                              className="slow-transition"
                            >
                              Logout
                            </Text>
                          ) : (
                            <ChakraLink
                              pl={"34px"}
                              mt={3}
                              w={"full"}
                              as={Link}
                              to={item.path}
                              textDecoration={"none"}
                              display={"flex"}
                              alignItems={"center"}
                              fontSize={"0.8rem"}
                              fontWeight={400}
                              color={
                                pathname === item.path || checkPath([item.path])
                                  ? "white"
                                  : "#C8D1E0"
                              }
                              _hover={{ color: "white" }}
                              className="slow-transition"
                            >
                              {item.title}
                            </ChakraLink>
                          )}
                        </Box>
                      ) : (
                        <Box key={ind}>
                          {item.title === "Logout" ? (
                            <Text
                              cursor={"pointer"}
                              pl={"34px"}
                              mt={6}
                              _first={{ mt: 4 }}
                              w={"full"}
                              onClick={() => {
                                logout();
                              }}
                              textDecoration={"underline"}
                              display={"flex"}
                              alignItems={"center"}
                              textStyle={"large"}
                              color={
                                pathname === item.path || checkPath([item.path])
                                  ? "white"
                                  : "#C8D1E0"
                              }
                              _hover={{ color: "white" }}
                              className="slow-transition"
                            >
                              Logout
                            </Text>
                          ) : (
                            <Text
                              pl={"34px"}
                              mt={6}
                              _first={{ mt: 4 }}
                              w={"full"}
                              onClick={() => {
                                navigate(item.path);
                                if (onClose) onClose();
                              }}
                              textDecoration={"underline"}
                              display={"flex"}
                              alignItems={"center"}
                              textStyle={"large"}
                              color={
                                pathname === item.path || checkPath([item.path])
                                  ? "white"
                                  : "#C8D1E0"
                              }
                              _hover={{ color: "white" }}
                              className="slow-transition"
                            >
                              {item.title}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
};

export default SideBar;
